import { Component, input, inject, OnInit, computed, output } from '@angular/core';
import { RouterLink, RouterOutlet } from '@angular/router';
import { patchState, signalState } from '@ngrx/signals';
import { MatToolbar } from '@angular/material/toolbar';
import { MatTooltip } from '@angular/material/tooltip';
import { MatDivider } from '@angular/material/divider';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import { AngularSplitModule, SplitAreaSize } from 'angular-split';
import { MatMiniFabButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { EnvironmentIndicatorComponent } from './environment-indicator/environment-indicator.component';
import { BreadcrumbsComponent } from './breadcrumbs/breadcrumbs.component';
import { AppSwitcherButtonComponent } from './app-switcher/app-switcher-button.component';
import { UserMenuComponent } from './user-menu/user-menu.component';
import { ShellSidebarNavComponent } from './sidebar-nav/shell-sidebar-nav.component';
import { HelpCenterButtonComponent } from './help-center/help-center-button.component';
import { NotificationModule } from '../../notifications/notification.module';
import { LocalStorageService } from '../../services/local-storage/local-storage.service';
import { FullPageLoadingPlaceholderComponent } from '../full-page-loading-placeholder/full-page-loading-placeholder.component';
import { AppRoute } from '../../types';
import { WsStatusComponent } from '../../notifications';
import { NotificationCenterToggleComponent } from '../../notifications/notification-center/notification-center-toggle.component';
import { EventModule } from '../../notifications/event.module';

type AppState = {
    sidenavSize: number;
    lockSidenavSize: boolean;
};

export const MINIMUM_SIDENAV_WIDTH_PX = 5;
export const MAXIMUM_SIDENAV_WIDTH_PX = 300;
export const DEFAULT_SIDENAV_WIDTH_PX = 200;
export const COLLAPSE_BREAKPOINT_SIDENAV_WIDTH_PX = 65;

@Component({
    selector: 'sphere-shell-app',
    templateUrl: './sphere-app-shell.component.html',
    styleUrl: './sphere-app-shell.component.scss',
    imports: [
        MatToolbar,
        AppSwitcherButtonComponent,
        RouterLink,
        MatTooltip,
        NotificationModule,
        EnvironmentIndicatorComponent,
        MatDivider,
        HelpCenterButtonComponent,
        NgTemplateOutlet,
        AngularSplitModule,
        NgClass,
        MatMiniFabButton,
        MatIcon,
        FullPageLoadingPlaceholderComponent,
        RouterOutlet,
        ShellSidebarNavComponent,
        BreadcrumbsComponent,
        UserMenuComponent,
        WsStatusComponent,
        NotificationCenterToggleComponent,
        EventModule
    ]
})
export class SphereAppShellComponent implements OnInit {
    static readonly SideNavSizeKey = 'sphere-sidenav-size';
    readonly MINIMUM_SIDENAV_WIDTH_PX = MINIMUM_SIDENAV_WIDTH_PX;
    readonly MAXIMUM_SIDENAV_WIDTH_PX = MAXIMUM_SIDENAV_WIDTH_PX;
    readonly DEFAULT_SIDENAV_WIDTH_PX = DEFAULT_SIDENAV_WIDTH_PX;
    readonly COLLAPSE_BREAKPOINT_SIDENAV_WIDTH_PX = COLLAPSE_BREAKPOINT_SIDENAV_WIDTH_PX;

    private readonly localStorage = inject(LocalStorageService);

    public readonly isShell = input<boolean>(true);
    public readonly hasSideNavigation = input<boolean>(true);
    public readonly currentApp = input<AppRoute | null>(null);
    public readonly noPaddingRoute = input<boolean>(false);
    public readonly routerNavigationLoading = input<boolean>(true);
    public readonly username = input.required<string>();
    public readonly canResetPassword = input(false);

    public readonly logout = output();
    public readonly openResetPasswordDialog = output();
    public readonly openSettingsDialog = output();

    public readonly state = signalState<AppState>({
        sidenavSize: DEFAULT_SIDENAV_WIDTH_PX,
        lockSidenavSize: false
    });

    public readonly sidenavSize = computed(() => {
        return this.state.sidenavSize();
    });

    public readonly collapsedSidenav = computed(() => {
        return this.sidenavSize() <= MINIMUM_SIDENAV_WIDTH_PX;
    });
    public readonly collapsedSidenavButtonState = computed(() => {
        return this.sidenavSize() <= COLLAPSE_BREAKPOINT_SIDENAV_WIDTH_PX;
    });

    ngOnInit() {
        this.localStorage.get<number>('portal', SphereAppShellComponent.SideNavSizeKey, { type: 'number' }).subscribe(sidenavSize => {
            if (typeof sidenavSize === 'number') {
                patchState(this.state, {
                    sidenavSize
                });
            }
        });
    }

    _toggleSidenavState() {
        if (this.sidenavSize() <= COLLAPSE_BREAKPOINT_SIDENAV_WIDTH_PX) {
            patchState(this.state, {
                sidenavSize: DEFAULT_SIDENAV_WIDTH_PX
            });
        } else {
            patchState(this.state, {
                sidenavSize: MINIMUM_SIDENAV_WIDTH_PX
            });
        }
    }

    saveSidenavState(event: SplitAreaSize[]) {
        let sidenavSize = event[0];
        const _contentSize = event[1];
        if (this.isNumberSize(sidenavSize) && sidenavSize > MAXIMUM_SIDENAV_WIDTH_PX) {
            sidenavSize = MAXIMUM_SIDENAV_WIDTH_PX;
        } else if (this.isNumberSize(sidenavSize) && sidenavSize < COLLAPSE_BREAKPOINT_SIDENAV_WIDTH_PX) {
            sidenavSize = MINIMUM_SIDENAV_WIDTH_PX;
        } else if (!sidenavSize || !this.isNumberSize(sidenavSize)) {
            sidenavSize = DEFAULT_SIDENAV_WIDTH_PX;
        }
        patchState(this.state, {
            sidenavSize
        });
        this.localStorage.set('portal', SphereAppShellComponent.SideNavSizeKey, sidenavSize).subscribe();
    }

    private isNumberSize(size: number | string): size is number {
        return typeof size === 'number';
    }
}
